import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { PageContainer } from 'components/styled';
import StandardPageHeader from 'components/StandardPageHeader';
import { StyledSimpleCard } from 'styles';
import StartRealmMigrationForm from './components/StartRealmMigrationForm';
import MigrationHistoryCard from './components/MigrationHistoryCard';
import MigrationStepsCard from './components/MigrationStepsCard';
import MigrationDashboardCard from './components/MigrationDashboardCard';
import MaintenanceWindowCard from './components/MaintenanceWindowCard';
import {
  isTerminalMigrationStatus,
  useRealmMigrations,
} from 'RealmModule/hooks/useRealmMigrations';

/**
 * Realm Migration.
 *
 * Moves one team from a source realm into a dedicated target realm, end to end: pre-flight
 * the plan, start the run, then watch it. The target realm must already exist and be
 * ready — this page validates that rather than provisioning it, so creating a realm stays
 * on Tenant & Realm Management where it already lives.
 *
 * The page owns two pieces of state the cards would otherwise each get wrong:
 *
 * - `sourceTenant`, because the form authenticates its pre-flight and start as that tenant
 *   while the monitoring queries would otherwise authenticate as whatever tenant the admin
 *   happens to be viewing. A cross-tenant migration would then be started successfully and
 *   never appear in the list the page just told the operator to watch.
 * - the migration list itself, so `selectedStatus` is re-derived on every poll. Holding the
 *   clicked row instead would freeze its status at click time, and the steps query — whose
 *   polling is gated on that status — would never stop.
 */
const RealmMigrationPage = () => {
  const [sourceTenant, setSourceTenant] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Undefined, not '': the hook then falls back to the tenant the admin is viewing, which
  // is the right default before a source tenant has been chosen.
  const tenant = sourceTenant || undefined;
  const { migrations, isLoading, isError, error } = useRealmMigrations({ tenant });

  // The selection names a row in the previous tenant's list, which this one will not contain.
  useEffect(() => {
    setSelectedId(null);
  }, [sourceTenant]);

  // Guarded on a real id: with `selectedId` null, `migrationLogId === selectedId` matches
  // any row that has no log id, and the page would report a migration as running before
  // the operator has selected anything.
  const selectedStatus =
    selectedId === null
      ? undefined
      : migrations.find((m) => m.migrationLogId === selectedId)?.status;

  return (
    <PageContainer>
      <Helmet title="Realm Migration" />
      <Grid container spacing={3} alignItems="stretch">
        <Grid size={12}>
          <StandardPageHeader title="Realm Migration" icon="Realm Migration" sx={{ mb: 4 }} />
        </Grid>

        <Grid size={12}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Run a migration
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <StyledSimpleCard>
            <StartRealmMigrationForm
              sourceTenant={sourceTenant}
              onSourceTenantChange={setSourceTenant}
            />
          </StyledSimpleCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MigrationStepsCard
            migrationLogId={selectedId}
            // A status we cannot find must mean "not running": a row that ages off the
            // list would otherwise reinstate the never-ending poll this derivation exists
            // to prevent.
            isRunning={!!selectedStatus && !isTerminalMigrationStatus(selectedStatus)}
            tenant={tenant}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Maintenance
          </Typography>
        </Grid>
        {/*
          Its own card, not a section of the form: a window is useful without a migration
          — announced ahead of time, held open across a manual finalize, extended when a
          run overruns — and only here can its start be something other than "now".
        */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <MaintenanceWindowCard />
        </Grid>

        <Grid size={12}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Monitoring
          </Typography>
        </Grid>
        <Grid size={12}>
          <MigrationHistoryCard
            migrations={migrations}
            isLoading={isLoading}
            isError={isError}
            error={error}
            tenant={tenant}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </Grid>
        <Grid size={12}>
          <MigrationDashboardCard />
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default RealmMigrationPage;
